/**
 * DotRail — b13i I1, generalised for per-block dots in b18a (item 3),
 * switched to OPT-IN in b18f (item 1).
 *
 * The left-margin "line with dots" decorative rail used on the primary pages
 * that lack Profile's bespoke section spine (Dex/Pokédex, Friends, Trade,
 * Calendar). Profile keeps its own section-anchored spine via SpineSection,
 * themed from the same tokens.
 *
 * b18f item 1 — dots are now OPT-IN. Earlier rounds dotted EVERY direct child
 * (or every child of a `data-dotrail-group`) that wasn't marked
 * `data-dotrail-skip`. That opt-out model meant spacer divs, layout wrappers,
 * CTA buttons, filter rows, and conditionally-rendered sections that returned
 * an element-but-no-heading all got stray dots floating in empty gaps with no
 * heading beside them (the Friends/Calendar bug). Chasing every skip marker was
 * fragile. Now a dot appears ONLY next to a child explicitly tagged
 * `data-dotrail-dot` — i.e. a real titled section/info block. Everything else
 * (spacers, wrappers, buttons, gaps) gets nothing by default.
 *
 * Each dotted block is wrapped in a `position: relative` row with the dot
 * absolutely placed at its top-left, so the dot tracks the block it belongs to
 * no matter where the block sits on the page.
 *
 * Markers:
 *   - `data-dotrail-dot` on a direct child (or a child inside a
 *     `data-dotrail-group`) → that block gets a top-anchored spine dot.
 *   - `data-dotrail-group` on a direct child → that element is a layout wrapper
 *     (e.g. a `space-y-5` stack); DotRail does not dot the wrapper itself but
 *     recurses into ITS children, dotting only the ones tagged
 *     `data-dotrail-dot`. This handles pages (Trade) that wrap all content in
 *     one stacking div rather than listing blocks flat.
 *   - `data-dotrail-skip` is still recognised but is now a no-op (opt-in means
 *     unmarked children already get no dot); it is kept so existing markup that
 *     still carries it does not need a sweep.
 *
 * The line and dots are the canonical inverse-theme element: the line flips
 * colour between light and dark via --rail-line; the per-block dot uses the
 * canonical --dot-accent token so it is visually identical to Profile's spine
 * dot and SectionHeadingDot. Everything is aria-hidden + pointer-events:none so
 * it never affects interaction or accessibility.
 *
 * Usage:
 *
 *   <DotRail>
 *     <div />                              // untagged: no dot
 *     <SomeSection data-dotrail-dot />     // titled block: gets a dot
 *     <div data-dotrail-group>             // wrapper: recurses into children
 *       <BlockA data-dotrail-dot />        // gets a dot
 *       <Spacer />                         // untagged: no dot
 *     </div>
 *   </DotRail>
 */
import {
  Children,
  cloneElement,
  isValidElement,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";

interface DotRailProps {
  children: ReactNode;
}

function hasMarker(el: ReactElement, marker: string): boolean {
  return (el.props as Record<string, unknown>)[marker] !== undefined;
}

/**
 * Wrap a block so a top-anchored Profile-style spine dot tracks its top.
 *
 * Geometry (matches Profile's SpineSection exactly): the DotRail container
 * carries `pl-5` (20px), so each block's content sits at the container's
 * content-left and the dot is pulled back to `left:-18px` so its 8px body spans
 * 2–10px from the container's outer edge and straddles the 6px rail line — the
 * same place Profile's spine dot sits.
 *
 * `z-10` lifts the dot above sibling block chrome so it is always visible, in
 * both light and dark, on every DotRail page. Colour stays on the canonical
 * `--dot-accent` token (no hard-coded green).
 */
function withDot(child: ReactNode, key?: string | number): ReactNode {
  return (
    <div className="relative" key={key}>
      <span
        aria-hidden
        data-testid="dot-rail-dot"
        className="pointer-events-none absolute top-2 z-10 w-2 h-2 rounded-full"
        style={{
          left: -18,
          background: "var(--dot-accent)",
          boxShadow: "0 0 6px var(--dot-accent)",
        }}
      />
      {child}
    </div>
  );
}

/** Decide whether/how a single direct child of the rail gets a dot. */
function renderRailChild(child: ReactNode, key: string | number): ReactNode {
  // Skip falsy children (conditionally-rendered sections) and plain text.
  if (!isValidElement(child)) return child;

  // Layout wrapper: don't dot the wrapper, recurse into its children and dot
  // only the ones explicitly tagged. We re-clone the wrapper preserving its
  // props/className so the page's own spacing is untouched.
  if (hasMarker(child, "data-dotrail-group")) {
    const inner = (child.props as { children?: ReactNode }).children;
    const dotted = Children.map(inner, (c, i) => renderRailChild(c, i));
    return cloneElement(child, { key }, dotted);
  }

  // Opt-in: only an explicitly tagged block gets a dot.
  if (hasMarker(child, "data-dotrail-dot")) return withDot(child, key);

  return child;
}

export function DotRail({ children }: DotRailProps) {
  return (
    <div
      /*
       * min-height is what makes the line reach the BOTTOM of the screen.
       *
       * The line is anchored `bottom-0` to this container, so on a page whose
       * content is shorter than the viewport — a raid lobby with two joiners, an
       * empty list — it stopped where the content stopped and left the spine
       * hanging in mid-air. The container now spans the viewport less the
       * shell's own chrome (sticky header plus tab bar, about 9rem between
       * them), so a short page's rail runs to the bottom edge and a long page's
       * is unaffected.
       *
       * A negative `bottom` on the line would have been the symmetric partner to
       * its `top-[-600px]`, but downward overflow adds scrollable area where
       * upward overflow does not, so it would have given every short page a few
       * hundred pixels of dead scroll.
       */
      className="relative pl-5 min-h-[calc(100dvh-9rem)]"
      data-testid="dot-rail"
      /*
       * How far a pinned band inside the rail may bleed, per side.
       *
       * RIGHT is the column's own 1rem — the rail adds no padding there, so that
       * reaches the screen edge. LEFT deliberately does NOT: the rail's 1.25rem
       * gutter is where the spine line (6px in) and its dots (2–10px) live, and
       * a band that bled the full 2.25rem covered them, so the timeline vanished
       * behind every pinned filter row. 0.5rem stops the band 2px clear of the
       * widest dot, which reads as the band respecting the gutter rather than
       * being short of the edge.
       */
      /* 13px left: the content is pl-5 (20px) in and the rail line's right
         edge sits 7px in (left-1.5 + 1px width), so a 13px bleed lands a
         pinned band's edge exactly ON the line — flush against the rail
         (2026-08-21 ask; was 0.5rem, which stopped 6px short). */
      style={{ "--band-bleed-left": "13px", "--band-bleed-right": "1rem" } as CSSProperties}
    >
      {/* The continuous vertical line down the gutter (left-1.5 = 6px), the
          same anchor Profile's spine line uses. `top-[-600px]` runs the line up
          behind the sticky header and off the top edge (matching Profile's
          home-spine) so it reads as a continuous timeline rather than starting
          with a hard cap in the gap below the header. bottom-0 anchors the tail. */}
      <span
        aria-hidden
        className="pointer-events-none absolute top-[-600px] bottom-0 left-1.5 w-px"
        style={{ background: "var(--rail-line)", opacity: 0.4 }}
      />
      {Children.map(children, (child, i) => renderRailChild(child, i))}
    </div>
  );
}
